// REFACTORED: 2026-02-06
// FROM src/components/myInsights/MyInsightsPage.tsx
// Top filters: FilterStripLayout with Duration + Project codes (apply/reset via useAppliedFilters).

import React, { useMemo, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { dashboardApi, getTableSyncStatus } from '../services/api';
import LastSyncedLabel from '../components/common/LastSyncedLabel';
import { FilterRequest, ProjectData } from '../types/api';
import { getDefaultDuration } from '../utils/DateUtils';
import FilterStripLayout from '../components/filters/FilterStripLayout';
import MonthRangePicker from '../components/MonthRangePicker';
import SelectAllMultiSelect from '../components/common/SelectAllMultiSelect';
import { CACHE_TIME } from '../constants/QueryConstants';
import { useAppliedFilters } from '../hooks/useAppliedFilters';
import {
  LazyMyScoreCards,
  LazyMyUtilMetrics,
  LazyMyProjectMetrics,
  LazyMyDefaulterTable,
  LazyMyProjectInsightsTable,
  LazyMyInsightsSkillsCertsDriSection,
} from '../components/LazyComponents';
import { SEARCE_COLORS } from '../theme';
import { CLIENT_SUCCESS_REGIONAL_MANAGER_ROLE } from '../constants/BusinessConstants';
import { calculateDriTier } from '../utils/FormatUtils';
import MyProfileHeader from '../components/myInsights/MyProfileHeader';

const MyInsightsPage: React.FC = () => {
  const { user } = useAuth();
  const showAnomaliesTab = user?.role?.name === CLIENT_SUCCESS_REGIONAL_MANAGER_ROLE;
  const hasUserRefreshedRef = useRef(false);

  const defaultFilters = useMemo<FilterRequest>(() => ({
    ...getDefaultDuration(),
    project_codes: [],
  }), []);
  const {
    currentFilters: filters,
    appliedFilters: activeFilters,
    hasChanges: filtersHaveChanged,
    setCurrentFilters,
    applyFilters,
    resetAllFilters,
  } = useAppliedFilters<FilterRequest>({ initialFilters: defaultFilters });



  const queriesEnabled = !!user?.email;

  const { data: syncData, isLoading: syncLoading } = useQuery({
    queryKey: ['sync-status'],
    queryFn: getTableSyncStatus,
    staleTime: 10 * 60 * 1000,
    enabled: queriesEnabled,
  });

  // Fetch user resource analytics data
  const { data: resourceAnalyticsData, isLoading: resourceAnalyticsLoading, error: resourceAnalyticsError } = useQuery({
    queryKey: ['my-resource-analytics', user?.email, activeFilters],
    queryFn: () => dashboardApi.getMyResourceAnalytics(activeFilters),
    enabled: queriesEnabled,
    staleTime: CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
  //dri-insights
  const { data: driinsights, isLoading: driinsightsLoading, error: driinsightsError } = useQuery({
    queryKey: ['dri-insights', user?.email],
    queryFn: () => dashboardApi.getMyDriInsights(),
    enabled: queriesEnabled,
    staleTime: CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  // Fetch user profile from happier data to get actual BU (source of truth)
  const { data: userProfile } = useQuery({
    queryKey: ['user-profile-happier', user?.email],
    queryFn: async () => {
      if (!user?.email) return null;
      return await dashboardApi.getUserProfile();
    },
    enabled: queriesEnabled,
  });

  const isClientSuccess = useMemo(() => {
    // Use BU from happier data (source of truth) instead of assigned BUs
    return userProfile?.business_unit === 'Client Success';// || userProfile?.user_email === 'ananthapadmanabhan.a@searce.com' || userProfile?.user_email === 'dhruvi.sharma@searce.com';
  }, [userProfile]);


  const { data: skillBadgesData, isLoading: skillBadgesLoading, error: skillBadgesError } = useQuery({
    queryKey: ['my-skill-badges', user?.email],
    queryFn: () => dashboardApi.getMySkillBadges(),
    enabled: queriesEnabled,
    staleTime: CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
  // console.log(skillBadgesData);
  const { data: certificationsData, isLoading: certificationsLoading, error: certificationsError } = useQuery({
    queryKey: ['my-certifications', user?.email],
    queryFn: () => dashboardApi.getMyCertifications(),
    enabled: queriesEnabled,
    staleTime: CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const { data: competencyProgressData, isLoading: competencyProgressLoading, error: competencyProgressError } = useQuery({
    queryKey: ['my-competency-progress', user?.email],
    queryFn: () => dashboardApi.getMyCompetencyProgress(),
    enabled: queriesEnabled,
    staleTime: CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  // Fetch DRI registration status

  const driRegistrationStatus = useMemo(() => {
    return {
      is_registered: driinsights?.is_registered ?? false,
      user_email: driinsights?.user_email ?? '',
    };
  }, [driinsights]);

  // Fetch defaulter (timesheet compliance) data
  const { data: defaulterData, isLoading: defaulterLoading, error: defaulterError } = useQuery({
    queryKey: ['my-defaulter-data', user?.email],
    queryFn: () => dashboardApi.getMyDefaulterData(),
    enabled: queriesEnabled,
    staleTime: CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
  const { data: projectData, isLoading: projectLoading, error: projectError } = useQuery({
    queryKey: ['my-project-data', user?.email, activeFilters],
    queryFn: () => dashboardApi.getMyProjectData(activeFilters),
    enabled: queriesEnabled,
    staleTime: CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const handleFiltersChange = (newFilters: FilterRequest) => setCurrentFilters(newFilters);

  const handleRefresh = () => {
    hasUserRefreshedRef.current = true;
    applyFilters();
  };

  const handleClear = () => {
    const nextDefaults: FilterRequest = { ...getDefaultDuration(), project_codes: [] };
    resetAllFilters(nextDefaults);
    hasUserRefreshedRef.current = true;
  };

  const insightsData = useMemo(
    () => ({
      projects: projectData?.projects ?? [],
      dri_insights: Array.isArray(driinsights) ? driinsights : (driinsights?.dri_insights ?? []),
      allocation_status: projectData?.allocation_status ?? '',
    }),
    [projectData?.projects, projectData?.allocation_status, driinsights]
  );

  const maxDriTier = useMemo(() => {
    const rows = insightsData.dri_insights;
    if (!rows.length) return null;
    const maxDri = Math.max(...rows.map((x: { total_dri?: number }) => Number(x.total_dri) || 0));
    return calculateDriTier(maxDri);
  }, [insightsData.dri_insights]);

  const projectMetricsInsightsData = useMemo(
    () => ({
      projects: projectData?.projects ?? [],
      certifications_and_dri: {
        certification_details: certificationsData?.certifications ?? [],
        skills_details: skillBadgesData?.skill_badges ?? [],
        dri_summary: { dri_status: '', projects_as_per_dri: 0 },
        expiry_status: [],
        certification_providers: [],
        recommendations: [],
      },
    }),
    [projectData?.projects, certificationsData?.certifications, skillBadgesData?.skill_badges]
  );
  const projectOptions = useMemo(
    () => (projectData?.projects ?? []).map((p: ProjectData) => ({
      value: p.project_code,
      label: p.project_name?.trim() ? `${p.project_code} - ${p.project_name}` : p.project_code,
    })),
    [projectData?.projects]
  );

  const filterColBase = {
    flex: '1 1 160px',
    minWidth: 112,
    maxWidth: 220,
    display: 'flex',
    alignItems: 'flex-end',
  } as const;
  const filterColDurationSx = { ...filterColBase, flex: '1.15 1 180px', maxWidth: 240 } as const;
  const filterColProjectSx = { ...filterColBase, flex: '1 1 200px', maxWidth: 340 } as const;

  return (
    <Box sx={{ p: 3, backgroundColor: SEARCE_COLORS.background, minHeight: '100vh' }}>
      <MyProfileHeader
        fullName={userProfile?.full_name}
        businessUnit={userProfile?.business_unit}
        managerName={userProfile?.manager_name}
        squadRole={userProfile?.squad_role}
        driTier={maxDriTier}
        driRegistrationStatus={driRegistrationStatus}
        driRegistrationLoading={driinsightsLoading}
      />
      <Box sx={{ mb: 2 }}>
        <FilterStripLayout
          onClear={handleClear}
          onRefresh={handleRefresh}
          hasUnsavedChanges={filtersHaveChanged}
          dense
        >
          <Box sx={filterColDurationSx}>
            <Box sx={{ width: '100%', minWidth: 0 }}>
              <MonthRangePicker
                startMonth={filters.start_year != null && filters.start_month != null ? { year: filters.start_year, month: filters.start_month } : null}
                endMonth={filters.end_year != null && filters.end_month != null ? { year: filters.end_year, month: filters.end_month } : null}
                onChange={(start, end) => handleFiltersChange({
                  ...filters,
                  start_year: start?.year,
                  start_month: start?.month,
                  end_year: end?.year,
                  end_month: end?.month,
                })}
                label="Duration"
              />
            </Box>
          </Box>
          <Box sx={filterColProjectSx}>
            <Box sx={{ width: '100%', minWidth: 0 }}>
              <SelectAllMultiSelect
                label="Project Code"
                options={projectOptions}
                selectedValues={filters.project_codes ?? []}
                onChange={(vals) => handleFiltersChange({ ...filters, project_codes: vals })}
                allLabel="All projects"
              />
            </Box>
          </Box>
        </FilterStripLayout>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <LastSyncedLabel isoTimestamp={syncData?.my_insights} isLoading={syncLoading} />
      </Box>
      <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600, color: SEARCE_COLORS.textPrimary }}></Typography>
      <LazyMyScoreCards
        insightsData={insightsData}
        resourceAnalyticsData={resourceAnalyticsData}
        driRegistrationStatus={driRegistrationStatus}
        driRegistrationLoading={driinsightsLoading}
        squadRole={userProfile?.squad_role}
      />

      <LazyMyUtilMetrics
        resourceAnalyticsData={resourceAnalyticsData}
        resourceAnalyticsLoading={resourceAnalyticsLoading}
        resourceAnalyticsError={resourceAnalyticsError}
        activeFilters={activeFilters}
        userEmail={user?.email}
      />

      {/* <LazyMyProjectMetrics insightsData={projectMetricsInsightsData} isLoading={projectLoading} /> */}
      
    <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600, color: SEARCE_COLORS.textPrimary }}></Typography>


      <LazyMyProjectInsightsTable
        projects={projectData?.projects ?? []}
        isLoading={projectLoading}
        error={projectError}
        isClientSuccess={isClientSuccess}
        showAnomaliesTab={showAnomaliesTab}
      />

      <LazyMyInsightsSkillsCertsDriSection
        skillBadges={skillBadgesData?.skill_badges ?? []}
        skillBadgesLoading={skillBadgesLoading}
        skillBadgesError={skillBadgesError as Error | null | undefined}
        certificationDetails={certificationsData?.certifications ?? []}
        certificationsLoading={certificationsLoading}
        certificationsError={certificationsError as Error | null | undefined}
        driInsights={insightsData.dri_insights}
        competencyProgress={competencyProgressData?.competencies ?? []}
        competencyProgressLoading={competencyProgressLoading}
        competencyProgressError={competencyProgressError as Error | null | undefined}
      />

      <LazyMyDefaulterTable
        defaulterData={defaulterData}
        isLoading={defaulterLoading}
        error={defaulterError}
      />
    </Box>
  );
};

export default MyInsightsPage;
